import { ChevronLeft, ChevronRight, MapPin, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	CircleMarker,
	MapContainer,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import type { LatLngLiteral } from "leaflet";
import { useAuthSessionContext } from "./routing/auth-session-store";
import { useFetch } from "../hooks/useFetch";
import type {
	LocationData,
	PickedLocation,
	AddressSuggestion,
	SavedLocation,
} from "../core/types/tsx/components/wizard/ServiceRequestStepLocation.types";

/* ------------------------------------------------------------------ */
/*  Nominatim helpers                                                  */
/* ------------------------------------------------------------------ */

async function reverseGeocodeNominatim(
	coords: PickedLocation,
	signal?: AbortSignal,
): Promise<string | null> {
	const { lat, lng } = coords;
	const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json", "Accept-Language": "es" },
	});
	if (!res.ok) return null;
	const data = (await res.json()) as { display_name?: string };
	const displayName =
		typeof data.display_name === "string" ? data.display_name.trim() : "";
	return displayName || null;
}

async function searchAddressNominatim(
	query: string,
	signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
	const q = query.trim();
	if (!q) return [];
	const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(q)}`;
	const res = await fetch(url, {
		signal,
		headers: { Accept: "application/json", "Accept-Language": "es" },
	});
	if (!res.ok) return [];
	const data = (await res.json()) as Array<{
		display_name?: string;
		lat?: string;
		lon?: string;
	}>;
	if (!Array.isArray(data)) return [];
	return data
		.map((item) => {
			const label =
				typeof item.display_name === "string" ? item.display_name.trim() : "";
			const lat = Number(item.lat);
			const lng = Number(item.lon);
			if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
			return { label, lat, lng };
		})
		.filter((x): x is AddressSuggestion => x !== null);
}

/* ------------------------------------------------------------------ */
/*  Saved locations (API)                                              */
/* ------------------------------------------------------------------ */

const MAX_SAVED_LOCATIONS = 10;
const SAVED_LOCATIONS_PAGE_SIZE = 5;

type AddressDto = {
	id?: number;
	name_address?: string;
	street_address?: string;
	reference_address?: string;
	updated_at?: string;
	created_at?: string;
};

type AddressesResponse = {
	addresses?: AddressDto[];
};

const createLocationLabel = (location: LocationData) => {
	if (location.nombreDireccion.trim()) return location.nombreDireccion.trim();
	if (location.direccion.trim()) {
		const short =
			location.direccion.length > 30
				? location.direccion.slice(0, 30) + "..."
				: location.direccion;
		return short;
	}
	return "Dirección";
};

const parseAddressParts = (displayName: string) => {
	const segments = displayName
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean)
		.filter((s) => !/^\d{3,}$/.test(s));

	const country = segments.length > 0 ? segments[segments.length - 1] : "";
	const state = segments.length > 1 ? segments[segments.length - 2] : "";
	const city = segments.length > 2 ? segments[segments.length - 3] : "";
	const street = segments.length > 3 ? segments[0] : "";

	return { country, state, city, street };
};

/* ------------------------------------------------------------------ */
/*  Map sub-components                                                 */
/* ------------------------------------------------------------------ */

function MapClickHandler({ onPick }: { onPick: (coords: PickedLocation) => void }) {
	useMapEvents({
		click(e) {
			onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
		},
	});
	return null;
}

function MapFlyTo({ center, zoom }: { center: LatLngLiteral; zoom: number }) {
	const map = useMap();
	useEffect(() => {
		map.flyTo(center, zoom, { duration: 1.2 });
	}, [map, center, zoom]);
	return null;
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

type LocationPickerProps = {
	value: LocationData;
	onChange: (v: LocationData) => void;
	onConfirm: () => void;
	onClose: () => void;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LocationPicker({
	value,
	onChange,
	onConfirm,
	onClose,
}: LocationPickerProps) {
	const { session } = useAuthSessionContext();
	const userId = session?.user?.id ?? null;
	const { execute: fetchSavedAddresses } = useFetch<AddressesResponse | AddressDto[]>({
		endpoint: "/addresses/client/0",
		method: "GET",
		auto: false,
	});
	const { execute: createAddress } = useFetch<AddressDto, Record<string, unknown>>({
		endpoint: "/addresses",
		method: "POST",
		auto: false,
	});
	const { execute: updateAddress } = useFetch<AddressDto, Record<string, unknown>>({
		endpoint: "/addresses/0",
		method: "PATCH",
		auto: false,
	});
	const { execute: deleteAddress } = useFetch<unknown>({
		endpoint: "/addresses/0",
		method: "DELETE",
		auto: false,
	});

	const update = (key: keyof LocationData, val: string) =>
		onChange({ ...value, [key]: val });
	const updateLocation = useCallback(
		(next: Partial<LocationData>) => onChange({ ...value, ...next }),
		[onChange, value],
	);
	const errorText = "mt-1 text-xs text-red-600";

	const buildLocationPayload = (): LocationData => ({
		nombreDireccion: value.nombreDireccion,
		direccion: value.direccion,
		referencias: value.referencias,
	});

	/* ---- map state ---- */
	const [pickedLocation, setPickedLocation] = useState<PickedLocation | null>(null);
	const [mapPickError, setMapPickError] = useState<string | null>(null);
	const [geoStatus, setGeoStatus] = useState<"loading" | "done" | "error">("loading");
	const [flyTarget, setFlyTarget] = useState<{ center: LatLngLiteral; zoom: number }>({
		center: { lat: 10.4806, lng: -66.9036 },
		zoom: 9,
	});
	const hasGeolocatedRef = useRef(false);
	const reverseGeocodeAbortRef = useRef<AbortController | null>(null);
	const [reverseGeocodeStatus, setReverseGeocodeStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");

	/* ---- address autocomplete state ---- */
	const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
	const [addressSuggestStatus, setAddressSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const addressSuggestAbortRef = useRef<AbortController | null>(null);
	const addressSuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);

	/* ---- saved locations state ---- */
	const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
	const [activeSavedId, setActiveSavedId] = useState<number | null>(null);
	const [savedLocationError, setSavedLocationError] = useState<string | null>(null);
	const [loadingSavedLocations, setLoadingSavedLocations] = useState(false);
	const [savedLocationsPage, setSavedLocationsPage] = useState(0);

	const mapAddressToSavedLocation = useCallback(
		(address: AddressDto): SavedLocation | null => {
			if (typeof address.id !== "number") return null;
			const name =
				typeof address.name_address === "string"
					? address.name_address.trim()
					: "";
			const street =
				typeof address.street_address === "string"
					? address.street_address.trim()
					: "";
			const reference =
				typeof address.reference_address === "string"
					? address.reference_address.trim()
					: "";

			return {
				id: address.id,
				label: name || (street ? street.slice(0, 30) : "Dirección"),
				location: {
					nombreDireccion: name,
					direccion: street,
					referencias: reference,
				},
				coords: null,
				updatedAt:
					address.updated_at ?? address.created_at ?? new Date().toISOString(),
			};
		},
		[],
	);

	const savedLocationsTotalPages = Math.max(
		1,
		Math.ceil(savedLocations.length / SAVED_LOCATIONS_PAGE_SIZE),
	);
	const pageStart = savedLocationsPage * SAVED_LOCATIONS_PAGE_SIZE;
	const pagedSavedLocations = savedLocations.slice(
		pageStart,
		pageStart + SAVED_LOCATIONS_PAGE_SIZE,
	);

	/* ---- geolocation on mount ---- */
	useEffect(() => {
		if (hasGeolocatedRef.current) return;
		hasGeolocatedRef.current = true;
		if (!navigator.geolocation) {
			setGeoStatus("error");
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					setGeoStatus("error");
					return;
				}
				setFlyTarget({ center: { lat, lng }, zoom: 14 });
				setGeoStatus("done");
			},
			() => {
				setGeoStatus("error");
			},
			{ enableHighAccuracy: false, timeout: 4000 },
		);
	}, []);

	/* ---- cleanup ---- */
	useEffect(() => {
		return () => {
			reverseGeocodeAbortRef.current?.abort();
			addressSuggestAbortRef.current?.abort();
			if (addressSuggestDebounceRef.current)
				window.clearTimeout(addressSuggestDebounceRef.current);
		};
	}, []);

	/* ---- load saved locations ---- */
	useEffect(() => {
		if (!userId) {
			setSavedLocations([]);
			setActiveSavedId(null);
			setSavedLocationsPage(0);
			setSavedLocationError(null);
			return;
		}

		let cancelled = false;
		const loadAddresses = async () => {
			setLoadingSavedLocations(true);
			setSavedLocationError(null);
			try {
				const payload = await fetchSavedAddresses({
					endpoint: `/addresses/client/${userId}`,
				});
				if (cancelled) return;

				const rawList = Array.isArray(payload)
					? payload
					: Array.isArray(payload?.addresses)
						? payload.addresses
						: [];
				const mapped = rawList
					.map((address) => mapAddressToSavedLocation(address))
					.filter((item): item is SavedLocation => item !== null)
					.slice(0, MAX_SAVED_LOCATIONS);

				setSavedLocations(mapped);
				setSavedLocationsPage(0);
				setActiveSavedId((prev) =>
					prev && mapped.some((item) => item.id === prev) ? prev : null,
				);
			} catch {
				if (!cancelled) {
					setSavedLocations([]);
					setActiveSavedId(null);
					setSavedLocationError(
						"No se pudieron cargar las ubicaciones guardadas.",
					);
				}
			} finally {
				if (!cancelled) setLoadingSavedLocations(false);
			}
		};

		void loadAddresses();
		return () => {
			cancelled = true;
		};
	}, [fetchSavedAddresses, mapAddressToSavedLocation, userId]);

	useEffect(() => {
		if (savedLocationsPage <= savedLocationsTotalPages - 1) return;
		setSavedLocationsPage(Math.max(savedLocationsTotalPages - 1, 0));
	}, [savedLocationsPage, savedLocationsTotalPages]);

	/* ---- handlers ---- */

	const handleMapPick = async (coords: PickedLocation) => {
		setPickedLocation(coords);
		setMapPickError(null);
		setAddressSuggestions([]);
		updateLocation({
			direccion: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
		});

		reverseGeocodeAbortRef.current?.abort();
		const controller = new AbortController();
		reverseGeocodeAbortRef.current = controller;
		setReverseGeocodeStatus("loading");
		try {
			const address = await reverseGeocodeNominatim(coords, controller.signal);
			updateLocation({
				direccion:
					address ?? `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
			});
			setReverseGeocodeStatus(address ? "idle" : "error");
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError") return;
			setReverseGeocodeStatus("error");
		}
	};

	const handleAddressInput = (nextValue: string) => {
		update("direccion", nextValue);

		addressSuggestAbortRef.current?.abort();
		if (addressSuggestDebounceRef.current) {
			window.clearTimeout(addressSuggestDebounceRef.current);
			addressSuggestDebounceRef.current = null;
		}
		setAddressSuggestStatus("idle");
		const q = nextValue.trim();
		if (q.length < 3) {
			setAddressSuggestions([]);
			return;
		}

		addressSuggestDebounceRef.current = window.setTimeout(async () => {
			setAddressSuggestStatus("loading");
			const controller = new AbortController();
			addressSuggestAbortRef.current = controller;
			try {
				const results = await searchAddressNominatim(q, controller.signal);
				setAddressSuggestions(results);
				setAddressSuggestStatus("idle");
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setAddressSuggestions([]);
				setAddressSuggestStatus("error");
			}
		}, 350);
	};

	const handleSuggestionClick = (sug: AddressSuggestion) => {
		addressSuggestAbortRef.current?.abort();
		updateLocation({ direccion: sug.label });
		setPickedLocation({ lat: sug.lat, lng: sug.lng });
		setFlyTarget({ center: { lat: sug.lat, lng: sug.lng }, zoom: 16 });
		setMapPickError(null);
		setAddressSuggestions([]);
		setAddressSuggestStatus("idle");
	};

	const handleConfirm = () => {
		if (!pickedLocation) {
			setMapPickError("Selecciona un punto en el mapa para confirmar la ubicación");
			return;
		}
		onConfirm();
	};

	const applySavedLocation = async (entry: SavedLocation) => {
		updateLocation(entry.location);
		setActiveSavedId(entry.id);
		const index = savedLocations.findIndex((item) => item.id === entry.id);
		if (index >= 0)
			setSavedLocationsPage(Math.floor(index / SAVED_LOCATIONS_PAGE_SIZE));
		setSavedLocationError(null);

		if (entry.coords) {
			setPickedLocation(entry.coords);
			setFlyTarget({ center: entry.coords, zoom: 16 });
			setMapPickError(null);
			return;
		}

		if (!entry.location.direccion.trim()) {
			setPickedLocation(null);
			setMapPickError("Selecciona un punto en el mapa para confirmar la ubicación");
			return;
		}

		try {
			const suggestions = await searchAddressNominatim(entry.location.direccion);
			const first = suggestions[0];
			if (!first) {
				setPickedLocation(null);
				setMapPickError(
					"Selecciona un punto en el mapa para confirmar la ubicación",
				);
				return;
			}
			const coords = { lat: first.lat, lng: first.lng };
			setPickedLocation(coords);
			setFlyTarget({ center: coords, zoom: 16 });
			setMapPickError(null);
		} catch {
			setPickedLocation(null);
			setMapPickError("Selecciona un punto en el mapa para confirmar la ubicación");
		}
	};

	const handleSaveNew = async () => {
		if (!userId) return;
		if (savedLocations.length >= MAX_SAVED_LOCATIONS) {
			setSavedLocationError(
				`Solo puedes guardar hasta ${MAX_SAVED_LOCATIONS} ubicaciones.`,
			);
			return;
		}
		if (!value.direccion.trim()) {
			setSavedLocationError("Completa la dirección antes de guardar la ubicación.");
			return;
		}

		setSavedLocationError(null);
		try {
			const parts = parseAddressParts(value.direccion);
			const created = await createAddress({
				body: {
					user_id: userId,
					name_country: parts.country || "N/A",
					name_state: parts.state || "N/A",
					name_city: parts.city || "N/A",
					name_address:
						value.nombreDireccion.trim() ||
						createLocationLabel(buildLocationPayload()),
					street_address: value.direccion.trim(),
					reference_address: value.referencias.trim() || "N/A",
				},
			});

			if (!created) throw new Error("No se pudo crear la ubicación");

			if (typeof created.id === "number") {
				await updateAddress({
					endpoint: `/addresses/${created.id}/reactivate`,
					body: {},
				}).catch(() => null);
			}

			const mapped = mapAddressToSavedLocation(created);
			if (!mapped) throw new Error("No se pudo crear la ubicación");

			const next = [...savedLocations, mapped].slice(0, MAX_SAVED_LOCATIONS);
			setSavedLocations(next);
			setActiveSavedId(mapped.id);
			setSavedLocationsPage(
				Math.floor((next.length - 1) / SAVED_LOCATIONS_PAGE_SIZE),
			);
		} catch {
			setSavedLocationError("No se pudo guardar la ubicación.");
		}
	};

	const handleUpdate = async () => {
		if (!userId || !activeSavedId) return;
		if (!value.direccion.trim()) {
			setSavedLocationError(
				"Completa la dirección antes de actualizar la ubicación.",
			);
			return;
		}

		setSavedLocationError(null);
		try {
			const parts = parseAddressParts(value.direccion);
			const updated = await updateAddress({
				endpoint: `/addresses/${activeSavedId}`,
				body: {
					user_id: userId,
					name_country: parts.country || "N/A",
					name_state: parts.state || "N/A",
					name_city: parts.city || "N/A",
					name_address:
						value.nombreDireccion.trim() ||
						createLocationLabel(buildLocationPayload()),
					street_address: value.direccion.trim(),
					reference_address: value.referencias.trim() || "N/A",
				},
			});
			if (!updated) throw new Error("No se pudo actualizar la ubicación");
			const mapped = mapAddressToSavedLocation(updated);
			if (!mapped) throw new Error("No se pudo actualizar la ubicación");

			setSavedLocations((prev) =>
				prev.map((entry) => (entry.id === activeSavedId ? mapped : entry)),
			);
		} catch {
			setSavedLocationError("No se pudo actualizar la ubicación.");
		}
	};

	const handleDelete = async () => {
		if (!userId || !activeSavedId) return;
		setSavedLocationError(null);
		try {
			await deleteAddress({ endpoint: `/addresses/${activeSavedId}` });
			const next = savedLocations.filter((entry) => entry.id !== activeSavedId);
			setSavedLocations(next);
			setActiveSavedId(null);
		} catch {
			setSavedLocationError("No se pudo eliminar la ubicación.");
		}
	};

	/* ------------------------------------------------------------------ */
	/*  RENDER                                                             */
	/* ------------------------------------------------------------------ */

	return (
		<div className='flex h-full min-h-0 w-full flex-col bg-white'>
			<div className='flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-8 pb-6 sm:px-12 sm:py-10'>
				{/* Header */}
				<div className='relative'>
					<div className='text-center'>
						<h2 className='text-2xl font-semibold text-slate-800'>
							¿Dónde quieres filtrar el servicio?
						</h2>
						<p className='mt-2 text-sm text-slate-500'>
							Selecciona en el mapa o busca una dirección
						</p>
					</div>
					<button
						type='button'
						aria-label='Cerrar'
						onClick={onClose}
						className='cursor-pointer absolute right-0 top-0 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
					>
						<X className='h-5 w-5' />
					</button>
				</div>

				<div className='mx-auto mt-8 w-full max-w-6xl'>
					<div className='grid items-stretch gap-10 pb-6 lg:grid-cols-2'>
						{/* ---------- LEFT COLUMN: Map ---------- */}
						<div className='flex h-full flex-col'>
							{geoStatus === "loading" ? (
								<div className='mb-2 flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-500'>
									<span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent' />
									Obteniendo tu ubicación...
								</div>
							) : geoStatus === "error" ? (
								<div className='mb-2 rounded-xl bg-red-50 px-4 py-2 text-sm text-red-600'>
									No se pudo obtener tu ubicación, vuelve a abrir el buscador o contacta con soporte.
								</div>
							) : null}
							<div className='mt-6 flex-1 overflow-hidden rounded-2xl bg-slate-200'>
								<div className='h-full min-h-96 w-full'>
									<MapContainer
										center={flyTarget.center}
										zoom={flyTarget.zoom}
										scrollWheelZoom
										className='h-full w-full'
									>
										<TileLayer
											attribution='&copy; OpenStreetMap contributors'
											url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
										/>
										<MapFlyTo
											center={flyTarget.center}
											zoom={flyTarget.zoom}
										/>
										<MapClickHandler onPick={(coords) => { void handleMapPick(coords); }} />
										{pickedLocation ? (
											<CircleMarker
												center={{
													lat: pickedLocation.lat,
													lng: pickedLocation.lng,
												}}
												radius={8}
											/>
										) : null}
									</MapContainer>
								</div>
							</div>
							{reverseGeocodeStatus === "loading" ? (
								<p className='mt-2 text-xs text-slate-500'>
									Buscando dirección...
								</p>
							) : reverseGeocodeStatus === "error" ? (
								<p className='mt-2 text-xs text-slate-500'>
									No se pudo obtener una dirección exacta. Se guardaron coordenadas.
								</p>
							) : null}
							{mapPickError ? (
								<p className={errorText}>{mapPickError}</p>
							) : null}
						</div>

						{/* ---------- RIGHT COLUMN: Form ---------- */}
						<div className='flex h-full flex-col'>
							{userId ? (
								<>
									<div className='text-base font-medium text-slate-700'>
										¿Es una ubicación guardada previamente?
									</div>

									{loadingSavedLocations ? (
										<p className='mt-2 text-xs text-slate-500'>
											Cargando ubicaciones guardadas...
										</p>
									) : null}

									<div className='mt-5 flex flex-wrap items-start gap-6'>
										{pagedSavedLocations.map((entry) => {
											const isActive = entry.id === activeSavedId;
											return (
												<button
													key={entry.id}
													type='button'
													className='cursor-pointer flex w-20 flex-col items-center gap-2'
													aria-label={entry.label}
													onClick={() => {
														void applySavedLocation(entry);
													}}
												>
													<span
														className={
															"flex h-14 w-14 items-center justify-center rounded-full transition " +
															(isActive
																? "bg-primary text-white"
																: "bg-secondary text-primary")
														}
													>
														<MapPin className='h-7 w-7' />
													</span>
													<span
														className={
															"text-center text-xs leading-tight " +
															(isActive
																? "text-primary font-medium"
																: "text-slate-500")
														}
													>
														{entry.label}
													</span>
												</button>
											);
										})}

										{savedLocations.length < MAX_SAVED_LOCATIONS ? (
											<button
												type='button'
												className='cursor-pointer flex w-20 flex-col items-center gap-2'
												aria-label='Nuevo'
												onClick={() => {
													setActiveSavedId(null);
													setSavedLocationError(null);
												}}
											>
												<span className='flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-primary'>
													<span className='text-3xl font-light leading-none'>
														+
													</span>
												</span>
												<span className='text-xs text-slate-500'>
													Nuevo
												</span>
											</button>
										) : null}
									</div>

									{savedLocations.length > SAVED_LOCATIONS_PAGE_SIZE ? (
										<div className='mt-4 flex items-center justify-between'>
											<button
												type='button'
												onClick={() =>
													setSavedLocationsPage((page) =>
														Math.max(page - 1, 0),
													)
												}
												disabled={savedLocationsPage === 0}
												className='cursor-pointer inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50'
											>
												<ChevronLeft className='h-3.5 w-3.5' />
												Anterior
											</button>
											<span className='text-xs text-slate-500'>
												Página {savedLocationsPage + 1} de{" "}
												{savedLocationsTotalPages}
											</span>
											<button
												type='button'
												onClick={() =>
													setSavedLocationsPage((page) =>
														Math.min(
															page + 1,
															savedLocationsTotalPages - 1,
														),
													)
												}
												disabled={
													savedLocationsPage >=
													savedLocationsTotalPages - 1
												}
												className='cursor-pointer inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50'
											>
												Siguiente
												<ChevronRight className='h-3.5 w-3.5' />
											</button>
										</div>
									) : null}
								</>
							) : null}

							<form className='mt-5 flex w-full flex-col gap-4'>
								{/* Nombre de la dirección */}
								<div className='flex flex-col gap-2'>
									<label
										htmlFor='lp-location-name'
										className='text-sm font-medium text-slate-700'
									>
										Nombre de la Dirección
									</label>
									<div className='relative'>
										<MapPin className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
										<input
											id='lp-location-name'
											type='text'
											value={value.nombreDireccion}
											maxLength={15}
											onChange={(e) =>
												update("nombreDireccion", e.target.value.slice(0, 15))
											}
											placeholder='Ej: Mi Casa, Oficina, Casa de mamá'
											className='h-12 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary placeholder:text-slate-400'
										/>
									</div>
								</div>

								{/* Dirección con autocompletado */}
								<div className='flex flex-col gap-2'>
									<label
										htmlFor='lp-location-address'
										className='text-sm font-medium text-slate-700'
									>
										Dirección *
									</label>
									<div className='relative'>
										<Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
										<input
											id='lp-location-address'
											type='text'
											value={value.direccion}
											onChange={(e) =>
												handleAddressInput(e.target.value)
											}
											onBlur={() => {
												window.setTimeout(
													() => setAddressSuggestions([]),
													150,
												);
											}}
											onKeyDown={(e) => {
												if (e.key === "Escape")
													setAddressSuggestions([]);
											}}
											placeholder='Busca una dirección o selecciona en el mapa'
											className='h-12 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary placeholder:text-slate-400'
										/>
										{addressSuggestions.length > 0 ? (
											<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg'>
												{addressSuggestions.map((sug) => (
													<li
														key={`${sug.lat},${sug.lng},${sug.label}`}
														className='border-b border-slate-100 last:border-b-0'
													>
														<button
															type='button'
															className='w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
															onMouseDown={(evt) => {
																evt.preventDefault();
																handleSuggestionClick(sug);
															}}
														>
															{sug.label}
														</button>
													</li>
												))}
											</ul>
										) : null}
									</div>
									{addressSuggestStatus === "loading" ? (
										<p className='mt-1 text-xs text-slate-500'>
											Buscando sugerencias...
										</p>
									) : null}
								</div>

								{/* Referencias */}
								<div className='flex flex-col gap-2'>
									<label className='text-sm font-medium text-slate-700'>
										Referencias Adicionales
									</label>
									<textarea
										rows={3}
										value={value.referencias}
										onChange={(e) =>
											update("referencias", e.target.value)
										}
										placeholder='Ej: Casa color blanco, portón negro, entre calle X y Y'
										className='rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary placeholder:text-slate-400'
									/>
								</div>
							</form>
						</div>
					</div>

					<div className={"mt-6 grid gap-3 " + (userId ? "lg:grid-cols-2" : "")}>
						{userId ? (
							<div className='flex h-10 flex-wrap items-center gap-3'>
								{activeSavedId ? (
									<>
										<button
											type='button'
											onClick={() => { void handleUpdate(); }}
											className='h-10 cursor-pointer rounded-full border border-primary/30 bg-white px-5 text-xs font-semibold text-primary transition hover:border-primary/50'
										>
											Actualizar ubicación
										</button>
										<button
											type='button'
											onClick={() => { void handleDelete(); }}
											className='h-10 cursor-pointer rounded-full border border-red-200 bg-white px-5 text-xs font-semibold text-red-600 transition hover:border-red-300'
										>
											Eliminar ubicación
										</button>
									</>
								) : (
									<button
										type='button'
										onClick={() => { void handleSaveNew(); }}
										className='h-10 cursor-pointer rounded-full border border-primary/30 bg-white px-5 text-xs font-semibold text-primary transition hover:border-primary/50'
									>
										Guardar ubicación
									</button>
								)}
							</div>
						) : null}

						<button
							type='button'
							onClick={handleConfirm}
							className='h-10 w-full cursor-pointer rounded-full bg-primary px-6 text-sm font-semibold text-white shadow-md transition hover:bg-primary-hover'
						>
							Aplicar filtro de ubicación
						</button>
					</div>

					{savedLocationError ? (
						<p className={errorText}>{savedLocationError}</p>
					) : null}
				</div>
			</div>
		</div>
	);
}
